import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Lobby, LobbySource } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";

/**
 * What the party deals from beyond the local catalog — phase 19e-4.
 *
 * # This is the screen four server methods had been waiting for
 *
 * `add_playlist`, `contribute_library`, `remove_source` and `refresh_sources`
 * shipped with 19e-2b and **no client could reach any of them**; `Lobby.sources`
 * was broadcast to every lobby and nothing rendered it. Until this file, a party
 * could only be given a friend's playlist by writing a row by hand.
 *
 * # Who may do what, and why it is not simply "the host"
 *
 * Mirrored from `src/rpc/party/sources.rs`, which is where the rule actually
 * lives — this only has to avoid contradicting it by hiding a control somebody
 * is allowed to use:
 *
 * | | who |
 * |---|---|
 * | add a playlist | **anybody who can act** — a public playlist needs no OAuth seat, so a guest with no account can contribute by pasting a link |
 * | contribute a library | **only your own**, and only with Spotify connected |
 * | remove a source | the host, or whoever added it |
 * | refresh | the host — it queues real work against a rate-limited API |
 *
 * # ⚠️ Nothing here fetches anything
 *
 * Adding a source writes one row; the import is a background job. Ten playlists
 * is up to ninety paginated requests, so a host pasting a link never waits on
 * Spotify — and the numbers on each row do not move when the promise resolves.
 * The copy says so, because a count that stays at zero for a minute otherwise
 * reads as a failure.
 */
export function PartySources({
  lobby,
  you,
  isHost,
  spotify,
  onAddPlaylist,
  onContributeLibrary,
  onRemoveSource,
  onRefresh,
  busy = false,
}: {
  lobby: Lobby;
  /** Your **profile** id, which is what `source.added_by` now carries (19e-4). */
  you: string;
  isHost: boolean;
  /**
   * Whether this installation runs the integration, and whether *you* have
   * connected — from `GET /api/spotify/status`, asked by the page.
   *
   * 🔴 **Per viewer, so it cannot ride on `Lobby`**, which is one payload
   * broadcast to everyone. `null` while the answer is in flight: the library
   * toggle is hidden rather than shown disabled, because a control that appears
   * and then becomes pressable reads as a glitch on a screen a room is watching.
   */
  spotify: { configured: boolean; connected: boolean; scopesCurrent: boolean } | null;
  onAddPlaylist: (link: string) => void;
  onContributeLibrary: (contribute: boolean) => void;
  onRemoveSource: (kind: string, refId: string) => void;
  onRefresh: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [pasted, setPasted] = useState("");

  // A library source you added is your own contribution, and the toggle reads
  // off it rather than off a second piece of state — one source of truth for
  // "am I contributing", and it survives another device changing it.
  const yourLibrary = lobby.sources.find(
    (source) => source.kind === "library" && source.added_by === you,
  );

  // Nothing to say about an integration this installation does not run. Not the
  // same as "you are not connected", which is worth a sentence and a link.
  if (spotify !== null && !spotify.configured) {
    return null;
  }

  const submit = () => {
    const link = pasted.trim();
    if (link === "" || busy) {
      return;
    }
    // Sent raw. The server parses a share link, a `spotify:` URI or a bare id
    // and answers a bad one with its own sentence per cause — validating the
    // shape here would be a second parser to keep in step, and the reason four
    // distinct causes get four messages is that "that did not work" is what
    // makes somebody paste the same broken link three times.
    onAddPlaylist(link);
    setPasted("");
  };

  return (
    <section aria-label={t("sources.label")} data-testid="party-sources">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{t("sources.heading")}</h2>
        {isHost && lobby.sources.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={onRefresh}
            className="text-xs text-flash-500 underline disabled:opacity-50"
          >
            {t("sources.refresh")}
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-white/40">{t("sources.lead")}</p>

      {lobby.sources.length === 0 ? (
        <p className="mb-3 text-xs text-white/40">{t("sources.none")}</p>
      ) : (
        <ul className="mb-3 flex flex-col gap-1.5">
          {lobby.sources.map((source) => (
            <SourceRow
              key={`${source.kind}:${source.ref_id}`}
              source={source}
              canRemove={isHost || source.added_by === you}
              disabled={busy}
              onRemove={() => {
                onRemoveSource(source.kind, source.ref_id);
              }}
            />
          ))}
        </ul>
      )}

      {/* Anybody who can act, host or not — see the table above. */}
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="url"
          value={pasted}
          disabled={busy}
          aria-label={t("sources.add.label")}
          placeholder={t("sources.add.placeholder")}
          onChange={(event) => {
            setPasted(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }
          }}
          className="min-w-0 flex-1 rounded-lg border border-white/15 bg-stage-900 px-3 py-2 text-sm disabled:opacity-50"
        />
        <button
          type="button"
          disabled={busy || pasted.trim() === ""}
          onClick={submit}
          className="rounded-lg border border-flash-500 bg-flash-500/15 px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {t("sources.add.action")}
        </button>
      </div>

      {spotify !== null && (
        <LibraryToggle
          spotify={spotify}
          contributing={yourLibrary !== undefined}
          disabled={busy}
          onChoose={onContributeLibrary}
        />
      )}
    </section>
  );
}

/**
 * One source, with the two numbers that decide whether it is worth having.
 *
 * 🎯 **Per contributor, never a party average** — the whole reason these are on
 * a row rather than summed into a header. The mix-of-everyone default lets one
 * friend's obscure library dominate the undated ratio, and a host looking at one
 * number cannot tell whether everybody is at 38% or three people are at 80% and
 * one is at 2%. On a row, they can ask *that friend* for a different playlist.
 */
function SourceRow({
  source,
  canRemove,
  disabled,
  onRemove,
}: {
  source: LobbySource;
  canRemove: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const imported = source.songs > 0;

  return (
    <li className="flex items-center gap-2 rounded-lg border border-white/15 bg-stage-900 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          {source.label}
          {source.added_by_name !== null && source.kind === "playlist" && (
            <span className="ml-1 text-xs text-white/40">
              {t("sources.by", { name: source.added_by_name })}
            </span>
          )}
        </p>
        {/* ⚠️ Not yet imported is its own sentence, because zero and zero read
            the same: "we have not looked" and "we looked and found nothing" are
            different states and only one of them is fixed by pressing refresh. */}
        <p className="text-xs tabular-nums text-white/40">
          {imported
            ? t("sources.counts", {
                songs: source.songs,
                dated: source.dated,
                resolved: source.resolved,
              })
            : t("sources.pending")}
        </p>
      </div>
      {canRemove && (
        <button
          type="button"
          disabled={disabled}
          onClick={onRemove}
          aria-label={t("sources.remove.label", { name: source.label })}
          className={cn(
            "shrink-0 rounded-md border border-white/15 px-2 py-1 text-xs",
            "text-white/60 disabled:opacity-50",
          )}
        >
          {t("sources.remove.action")}
        </button>
      )}
    </li>
  );
}

/**
 * Contribute your own liked songs, or stop.
 *
 * 🔴 **Offered only when you actually have Spotify connected**, and this is not
 * cosmetic. The server accepts a library source from any signed-in member, but
 * the importer only reads libraries for accounts that are *still connected* — so
 * an unconnected member could add a source that silently imports nothing, on a
 * screen that reported it as added. That is a control with no effect, which is
 * the shape this project found four times in one session.
 *
 * ⚠️ A stale grant gets its own message rather than being treated as connected:
 * the alternative is a 403 from a playlist read that nobody can interpret.
 *
 * 📌 **`configured` is not asked here**, and the narrowed prop type says so: the
 * parent returns `null` for the whole section on an installation that does not
 * run Spotify, so by the time this renders the integration exists. Taking the
 * wider object would invite a second, redundant guard — and a reader into
 * wondering which of the two was the real one.
 *
 * # The link opens a tab, and the lobby re-asks when you come back — 20g
 *
 * Connecting is a navigation, not a fetch (see [`SpotifyPanel`]), and this one
 * happens **from inside a live party**. A same-tab navigation drops the lobby,
 * closes the socket, and leaves somebody to find their way back mid-round; and
 * without the re-ask in `LobbyPanel`, connecting in another tab left this panel
 * still saying "Connect" until a reload. Between them the advice this screen
 * gives is advice that works.
 */
function LibraryToggle({
  spotify,
  contributing,
  disabled,
  onChoose,
}: {
  /** Narrower than the caller holds: `configured` is the parent's gate. */
  spotify: { connected: boolean; scopesCurrent: boolean };
  contributing: boolean;
  disabled: boolean;
  onChoose: (contribute: boolean) => void;
}) {
  const { t } = useTranslation();

  if (!spotify.connected) {
    return (
      <p className="mt-3 text-xs text-white/40">
        {t("sources.library.disconnected")}{" "}
        <SettingsLink label={t("sources.library.connect")} />
      </p>
    );
  }

  if (!spotify.scopesCurrent) {
    return (
      <p className="mt-3 text-xs text-coin-400">
        {t("sources.library.stale")}{" "}
        <SettingsLink label={t("sources.library.reconnect")} />
      </p>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={contributing}
      disabled={disabled}
      onClick={() => {
        onChoose(!contributing);
      }}
      className={cn(
        "mt-3 w-full rounded-lg border px-3 py-2 text-left text-sm disabled:opacity-50",
        contributing
          ? "border-flash-500 bg-flash-500/15"
          : "border-white/15 bg-stage-900",
      )}
    >
      <span className="font-medium">
        {contributing ? t("sources.library.withdraw") : t("sources.library.contribute")}
      </span>
      <span className="mt-0.5 block text-xs text-white/55">
        {t("sources.library.detail")}
      </span>
    </button>
  );
}

/**
 * The way out to `/settings`, from inside a party.
 *
 * ⚠️ **A new tab, deliberately.** Everywhere else in the app this would be a
 * router `Link`; here the page it leaves is a live lobby holding a socket and a
 * seat, and connecting Spotify is a detour of several screens through a consent
 * page. Coming back to find the party where you left it is the difference
 * between advice and an interruption.
 *
 * `rel="noreferrer"` alongside `noopener` because the destination is our own
 * origin either way and there is nothing to gain from leaking the party URL into
 * a `Referer` header that a future settings page might forward.
 */
function SettingsLink({ label }: { label: string }) {
  return (
    <a
      href="/settings"
      target="_blank"
      rel="noopener noreferrer"
      className="text-flash-500 underline"
    >
      {label}
    </a>
  );
}
